import { useState } from "react";
import { useNavigate } from "react-router";
import { describeError, isApiError } from "@/api/client";
import {
  Button,
  Dialog,
  DialogClose,
  Form,
  FormActions,
  InlineAlert,
  NumberField,
  TextField,
  useToast,
} from "@/components";
import { Decimal } from "@/lib/decimal";
import { formatInteger } from "@/lib/format";
import { useCreateBasket, type CoverageItem } from "./api";
import styles from "./KnowledgePage.module.css";

/** Turns a reference list's missing materials into a draft basket in purchasing. */
export function AddToBasketDialog({
  open,
  onOpenChange,
  listName,
  items,
  unresolvedCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listName: string;
  items: CoverageItem[];
  unresolvedCount: number;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add missing materials to a basket"
      description="Purchasing plans the order across suppliers, with shipping and duty, before anything is bought."
    >
      {open ? (
        <BasketForm
          listName={listName}
          items={items}
          unresolvedCount={unresolvedCount}
          onDone={() => onOpenChange(false)}
        />
      ) : null}
    </Dialog>
  );
}

function BasketForm({
  listName,
  items,
  unresolvedCount,
  onDone,
}: {
  listName: string;
  items: CoverageItem[];
  unresolvedCount: number;
  onDone: () => void;
}) {
  const create = useCreateBasket();
  const toast = useToast();
  const navigate = useNavigate();
  const [name, setName] = useState(`Missing from ${listName}`);
  const [grams, setGrams] = useState<string | null>("10");
  const [error, setError] = useState<string | null>(null);
  const materials = items.filter((item): item is CoverageItem & { material: NonNullable<CoverageItem["material"]> } =>
    Boolean(item.material),
  );

  function submit() {
    const amount = Decimal.tryFrom(grams);
    if (!name.trim()) {
      setError("Name the basket");
      return;
    }
    if (amount === null || amount.compare(0) <= 0) {
      setError("The amount must be more than 0 g");
      return;
    }
    setError(null);
    create.mutate(
      {
        name: name.trim(),
        lines: materials.map((item) => ({ material_id: item.material.id, neat_g: amount.toString() })),
      },
      {
        onSuccess: (basket) => {
          onDone();
          const id = basket.id;
          toast.success(`Basket created with ${formatInteger(materials.length)} materials`, {
            action: id
              ? { label: "Open basket", onClick: () => void navigate(`/purchasing/baskets/${id}`) }
              : undefined,
          });
        },
      },
    );
  }

  const unavailable =
    create.error && isApiError(create.error) && [404, 405, 501].includes(create.error.status)
      ? "Purchasing can’t take baskets yet on this server. Nothing was created; try again once it is available."
      : null;

  return (
    <Form onSubmit={submit}>
      <div className={styles.fields}>
        <p className={styles.muted}>
          {formatInteger(materials.length)} missing materials go in the basket.
          {unresolvedCount
            ? ` ${formatInteger(unresolvedCount)} unknown names are left out until they’re linked in Review.`
            : ""}
        </p>
        <TextField label="Basket name" name="name" value={name} onValueChange={setName} required />
        <NumberField
          label="Amount of each"
          name="neat_g"
          unit="g"
          min={0}
          step={1}
          description="Neat grams per material; purchasing picks pack sizes that cover it."
          value={grams}
          onValueChange={setGrams}
          error={error}
        />
        {create.error ? (
          <InlineAlert tone="danger" title="Couldn’t create the basket">
            {unavailable ?? describeError(create.error)}
          </InlineAlert>
        ) : null}
      </div>
      <FormActions>
        <DialogClose />
        <Button
          variant="primary"
          type="submit"
          pending={create.isPending}
          pendingLabel="Creating…"
          disabled={!materials.length}
        >
          Create basket with {formatInteger(materials.length)} materials
        </Button>
      </FormActions>
    </Form>
  );
}
